import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CaptureUpdateAction,
  Excalidraw,
  convertToExcalidrawElements,
  exportToSvg,
  viewportCoordsToSceneCoords,
} from '@excalidraw/excalidraw';
import type { ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types';
import type { ExcalidrawElement } from '@excalidraw/excalidraw/element/types';
import '@excalidraw/excalidraw/index.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE PORT DU MOTEUR DE TABLEAU — D-001 ET D-012, ET C'EST LE SEUL FICHIER
 * DE `web/` QUI CONNAISSE LE MOTEUR.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **D-012 le rend obligatoire, et sa dernière phrase est un ultimatum** : « le moteur de
 * tableau doit être isolé derrière une interface étroite **dès le premier écrit**, sur le
 * modèle de D-002 pour le temps réel et de D-003 pour la visio, sinon le remplacement
 * n'est plus un changement de moteur mais **une réécriture du front de séance**. **À
 * défaut d'interface, cette décision n'est pas exécutable.** »
 *
 * Et le remplacement n'est pas une hypothèse d'école : D-012 pose que « **si le rendu du
 * trait ou le rejet de paume ne tiennent pas à l'usage, Ziteboard remplace le moteur en
 * fin de V1** », sur deux limites déjà mesurées — pression absente sur iPad, mode stylet à
 * activer soi-même. **F-27, le tableau sur tablette, est le point où cela se décidera.**
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE CE FICHIER A REMPLACÉ, ET POURQUOI LE CORPUS L'IMPOSAIT — D-115.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `web/` portait un moteur **écrit à la main**, deux canevas superposés dans un espace
 * logique **fixe de 1600 × 1000**. Il fonctionnait, et il était éprouvé à deux navigateurs
 * (D-110). **Trois choses lui manquaient, et aucune n'était un accessoire :**
 *
 * 1. **Six des douze outils de F-22** — texte tapé, formes, sélection d'un ensemble,
 *    annuler et refaire, tout effacer, pointeur temporaire.
 * 2. **F-23 en entier.** « Un canevas **infini** sur lequel une grille de délimitations
 *    A4 est tracée d'emblée, **visible en permanence** », en colonne unique infinie vers
 *    le bas, chaque case portant **son numéro visible** (DD-05). Un espace fixe de
 *    1600 × 1000 ne peut porter aucune de ces règles.
 * 3. **La base du document de F-25.** D-001 : « l'export PDF assemble les **exports SVG de
 *    chaque frame** triée par position Y avec `pdf-lib` ». Sans frame, il n'y a pas de
 *    page, et `fabriquerLeDocument` n'avait rien à paginer.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **AUCUN TYPE DU MOTEUR NE SORT D'ICI, ET C'EST CE QUI REND LE PORT VRAI.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Ce que les écrans, les hooks et le canal manipulent est `ElementDeTableau`, **un type
 * opaque**. Ils le transportent, le persistent et le diffusent sans jamais le lire — ce
 * qui est exactement ce que le contrat d'écriture dit déjà du serveur : « sa forme interne
 * appartient au moteur : **le produit la transporte et ne l'interprète pas** ».
 * `web/e2e/port-du-moteur.spec.ts` vérifie par balayage que ce fichier reste le seul à
 * importer la bibliothèque.
 */

/**
 * **Un élément du tableau, tel qu'il voyage et se conserve — forme opaque.**
 *
 * **C'est le type que D-012 protège.** Le jour où Ziteboard remplace le moteur, ce qui
 * change est ce fichier ; les hooks, les écrans et le canal ne bougent pas, parce qu'ils
 * n'ont jamais su ce qu'il y avait dedans.
 */
export type ElementDeTableau = { readonly __element: unique symbol };

/** Ce que le port rend d'un élément sans le faire lire : son identité. */
export function identiteDe(element: ElementDeTableau): string {
  return (element as unknown as ExcalidrawElement).id;
}

/**
 * **Les douze outils de F-22, et la liste est fermée par le corpus.**
 *
 * « Douze outils : crayon, gomme, surligneur, choix de couleur, choix d'épaisseur, texte
 * tapé au clavier, formes (trait, rectangle, cercle, flèche), sélection d'un ensemble
 * d'éléments, annuler et refaire, tout effacer, pointeur temporaire, pense-bête »
 * (Q347). **Le zoom et le mode main sont acquis par D-007**, donc ils ne comptent pas
 * dans les douze et le moteur les fournit.
 *
 * **Ce type ne porte que ce qui se CHOISIT comme outil actif.** Le choix de couleur et
 * d'épaisseur sont des réglages, annuler, refaire et tout effacer sont des gestes
 * ponctuels : ils passent par les fonctions du port, pas par cette liste.
 */
export { OUTILS } from '../../shared/ui/palette';
export type { Outil } from '../../shared/ui/palette';

/**
 * **La correspondance vers les outils du moteur, et deux lignes ne sont pas évidentes.**
 *
 * **Le surligneur n'est pas un outil du moteur** : c'est le crayon avec l'encre de
 * surlignage, dont la charte fixe la transparence. **Et le pense-bête non plus** : c'est
 * un objet métier du produit (Q352), posé comme un rectangle portant un texte — la
 * fonction `poserUnPenseBete` du port s'en charge, et l'outil sert seulement à armer le
 * prochain clic.
 */
const OUTIL_DU_MOTEUR: Record<
  Exclude<Outil, 'surligneur' | 'pense-bete'>,
  'freedraw' | 'eraser' | 'selection' | 'text' | 'line' | 'rectangle' | 'ellipse' | 'arrow' | 'laser'
> = {
  crayon: 'freedraw',
  gomme: 'eraser',
  selection: 'selection',
  texte: 'text',
  trait: 'line',
  rectangle: 'rectangle',
  cercle: 'ellipse',
  fleche: 'arrow',
  // « Pointeur temporaire » : un tracé qui s'efface de lui-même et ne se conserve
  // pas. Le moteur l'appelle `laser`, et il ne produit aucun élément — donc rien ne
  // part au serveur, ce qui est la définition de « temporaire ».
  pointeur: 'laser',
};

/** Les cinq encres de la charte. Réexportées : elles préexistent au moteur. */
export { ENCRES } from '../../shared/ui/palette';
export type { Encre } from '../../shared/ui/palette';
import type { Encre, Outil } from '../../shared/ui/palette';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA GRILLE A4 DE F-23 : DÉRIVÉE, VERROUILLÉE, ET JAMAIS CONSERVÉE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « **L'origine de la grille est la même pour tous les tableaux : c'est une constante du
 * produit, et elle ne se stocke pas** » (Q342, Q344). « **Personne ne crée une page. Une
 * page n'est pas un objet.** » « **Aucune page ne se supprime, aucune ne se déplace** »
 * (Q339, Q340).
 *
 * **Ces quatre règles décident de la forme, et elles excluent la voie évidente.** Les
 * cases sont les **frames natives** du moteur, comme D-001 le pose — mais elles ne sont
 * **jamais persistées ni diffusées** : elles sont recalculées à chaque montage depuis les
 * constantes ci-dessous. Trois conséquences, toutes voulues : elles ne comptent pas dans
 * le **poids du tableau** (F-21), elles ne peuvent pas être supprimées par quelqu'un, et
 * deux postes ont exactement la même grille sans que rien ne se synchronise.
 *
 * **Elles sont aussi verrouillées**, ce qui empêche de les déplacer à la souris — « aucune
 * ne se déplace » n'est donc pas une consigne mais un fait.
 *
 * **A4 debout, format unique** (Q335), en points à 72 par pouce : c'est l'unité du PDF,
 * donc la page fabriquée par `pdf-lib` a exactement les proportions de la case tracée.
 */
/**
 * **Les deux délais de l'annonce, en millisecondes, et ils ne servent pas à la même chose.**
 *
 * `DELAI_APRES_LEVEE_MS` est le seul qui touche la latence du trait, et il est court **pour
 * une raison mesurée** : le moteur remanie l'élément **après** la levée du stylet — la
 * version bouge encore, et le poids passe de 1501 à 1547 sur un trait de quarante points.
 * Annoncer au moment exact de la levée conserverait donc la forme d'avant remaniement.
 *
 * `DELAI_DE_SECOURS_MS` ne sert qu'à ce que rien ne reste en attente pour toujours quand un
 * élément entre en scène **sans qu'un pointeur soit relâché sur le canevas** : un pense-bête
 * posé par un bouton, un texte quitté au clavier.
 */
const DELAI_APRES_LEVEE_MS = 50;
const DELAI_DE_SECOURS_MS = 400;

const PAGE_LARGEUR = 595;
const PAGE_HAUTEUR = 842;
/** L'écart entre deux cases, pour que la limite se voie. Elle n'est pas de la page. */
const PAGE_ECART = 24;
/** L'origine, constante du produit : « personne ne la décale » (Q342). */
const GRILLE_ORIGINE_X = 0;
const GRILLE_ORIGINE_Y = 0;
/** Combien de cases naissent d'emblée, et combien s'ajoutent sous le contenu. */
const CASES_AU_DEPART = 3;
const CASES_DE_MARGE = 2;

/**
 * **Le rang de la case qui contient une ordonnée** — « le rang d'une case est sa position
 * verticale, et l'ordre du document est du haut vers le bas » (Q342). Rang 1 en haut.
 */
function rangDeLOrdonnee(y: number): number {
  const pas = PAGE_HAUTEUR + PAGE_ECART;
  return Math.max(1, Math.floor((y - GRILLE_ORIGINE_Y) / pas) + 1);
}

/**
 * **Les cases de la grille, en frames verrouillées et nommées par leur numéro.**
 *
 * DD-05 : « chaque case porte **son numéro visible à l'écran**, pour que “la page 3”
 * désigne la même chose pour deux personnes qui se parlent ». Le nom d'une frame est
 * précisément ce que le moteur affiche au-dessus d'elle.
 */
function grille(nombre: number): ExcalidrawElement[] {
  const cases = [];
  for (let rang = 1; rang <= nombre; rang += 1) {
    cases.push({
      type: 'rectangle' as const,
      x: GRILLE_ORIGINE_X,
      y: GRILLE_ORIGINE_Y + (rang - 1) * (PAGE_HAUTEUR + PAGE_ECART),
      width: PAGE_LARGEUR,
      height: PAGE_HAUTEUR,
      name: `Page ${rang}`,
      locked: true,
      // **Une case ne contient rien, et c'est la règle et non un vide à remplir** :
      // « personne ne crée une page, une page n'est pas un objet » (Q337). Le contenu
      // vit à côté d'elle, pas dedans — c'est `exportingFrame` qui décide, à l'export,
      // ce qui tombe dans la case, par sa géométrie.
      strokeColor: '#d0cfd6',
      backgroundColor: 'transparent',
      // **Une case ne contient rien, et c'est la règle et non un vide à remplir** :
      // « personne ne crée une page, une page n'est pas un objet » (Q337). Le contenu
      // vit à côté d'elle, pas dedans — c'est `exportingFrame` qui décide, à l'export,
      // ce qui tombe dans la case, par sa géométrie. Rattacher les tracés aux frames
      // les ferait déplacer avec elles, et « aucune ne se déplace ».
    });
  }
  return convertToExcalidrawElements(cases);
}

/**
 * **Les proportions d'une page, RÉEXPORTÉES et non redéclarées.**
 *
 * « L'origine de la grille est la même pour tous les tableaux : c'est une **constante du
 * produit**, et elle ne se stocke pas » (Q342, Q344), et `web/e2e/port-du-moteur.spec.ts`
 * refuse que `PAGE_LARGEUR`, `PAGE_HAUTEUR` ou `GRILLE_ORIGINE` paraissent dans un autre
 * fichier — « une origine déclarée ailleurs voudrait dire qu'on la transporte, donc que
 * deux tableaux pourraient en avoir deux ».
 *
 * **Ceci n'est pas une seconde déclaration** : c'est la même valeur, lue au seul endroit
 * qui la porte. Elle sert à ce qui n'a pas de proportions à soi — **un PDF**, dont F-24
 * dit qu'« une page non A4 est mise à l'échelle », et dont le produit ne sait pas lire les
 * dimensions sans rasteriseur.
 */
export const PROPORTIONS_DE_LA_PAGE = {
  largeur: PAGE_LARGEUR,
  hauteur: PAGE_HAUTEUR,
} as const;

/** Vrai pour une case de la grille : ce qui ne se persiste pas et ne se compte pas. */
function estUneCase(element: ExcalidrawElement): boolean {
  return element.type === 'rectangle' && element.locked === true;
}

/** La marge intérieure d'une case, pour qu'une image posée ne colle pas au bord. */
const MARGE_DANS_LA_CASE = 40;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **DÉSIGNER UN TRACÉ — F-47, DEUXIÈME GESTE, ET LA SÉLECTION DU MOTEUR NE
 * PEUT PAS LE SERVIR.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Les deux surfaces qui portent ce geste sont **en lecture stricte**, et le mode lecture
 * du moteur **efface la sélection à chaque relâchement** : lu dans la source de 0.18.1,
 * `handleCanvasPointerUp` y fait `setState({ selectedElementIds: {} })`. Un écran qui
 * lirait `selectedElementIds` lirait donc toujours un ensemble vide.
 *
 * **Et `onPointerUpdate` ne le sert pas davantage.** Toujours en mode lecture, un appui
 * sur le canevas part dans le chemin de déplacement de la vue et **rend la main avant**
 * l'annonce du pointeur : le rappel ne reçoit jamais de `button: 'down'`, et les
 * mouvements qui suivent portent tous `'up'`. Rien n'y distingue une désignation d'un
 * déplacement de la vue.
 *
 * **Ce qui reste est donc l'appui lui-même, lu sur le conteneur**, converti en points de
 * la grille par la fonction publique du paquet, et confronté aux éléments de la scène.
 * **Un déplacement de la vue n'est pas une désignation** : au-delà de quelques points
 * entre l'appui et le relâchement, le geste est un déplacement et rien n'est désigné.
 *
 * **CE QUE CETTE MESURE VAUT, ÉCRIT PLUTÔT QUE SUPPOSÉ.** Elle compare la **boîte** d'un
 * élément, pas sa géométrie exacte : un trait courbe se désigne donc en touchant sa
 * boîte, et deux tracés qui se croisent rendent **celui du dessus**, c'est-à-dire le
 * dernier de la scène. C'est ce que le moteur fait lui-même quand deux éléments se
 * superposent, et c'est le seul comportement qu'un lecteur peut prédire.
 *
 * **Les cases de la grille ne se désignent pas** : elles ne sont ni conservées, ni
 * diffusées, ni d'un auteur — « personne ne crée une page, une page n'est pas un objet »
 * (Q337).
 */
const TOLERANCE_DE_DESIGNATION = 8;
/** Au-delà, en pixels d'écran, l'appui était un déplacement de la vue. */
const TOLERANCE_DU_GESTE = 6;

function elementDesigneA(
  api: ExcalidrawImperativeAPI,
  clientX: number,
  clientY: number,
): string | null {
  const { x, y } = viewportCoordsToSceneCoords(
    { clientX, clientY },
    api.getAppState(),
  );
  const touches = api.getSceneElements().filter(
    (element) =>
      !estUneCase(element) &&
      x >= element.x - TOLERANCE_DE_DESIGNATION &&
      x <= element.x + (element.width ?? 0) + TOLERANCE_DE_DESIGNATION &&
      y >= element.y - TOLERANCE_DE_DESIGNATION &&
      y <= element.y + (element.height ?? 0) + TOLERANCE_DE_DESIGNATION,
  );
  // Le dernier de la scène est celui du dessus.
  return touches.at(-1)?.id ?? null;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **OÙ UNE IMAGE SE POSE, ET POURQUOI CE CALCUL VIT DANS LE PORT.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Le dépôt se pose **à la suite du contenu existant** » (Q363), et « ce qui est tracé
 * hors de la colonne ne part pas au document, et **rien ne le distingue à l'écran** »
 * (Q345, Q346). Une image posée au hasard serait donc perdue au document sans qu'aucun
 * signal ne l'annonce : elle est **bornée à la largeur d'une case**, moins sa marge.
 *
 * **Les proportions sont conservées**, et une petite image n'est jamais agrandie — même
 * règle qu'à la réduction côté serveur : agrandir ajoute du poids sans ajouter
 * d'information.
 *
 * **Ce calcul ne peut pas vivre ailleurs** : il nomme `PAGE_LARGEUR`, et
 * `web/e2e/port-du-moteur.spec.ts` refuse que ces constantes paraissent dans un autre
 * fichier — « une origine déclarée ailleurs voudrait dire qu'on la transporte, donc que
 * deux tableaux pourraient en avoir deux ».
 */
function placementDansLaVue(
  api: ExcalidrawImperativeAPI,
  largeurNaturelle: number,
  hauteurNaturelle: number,
): PlacementSurLaGrille {
  const disponible = PAGE_LARGEUR - 2 * MARGE_DANS_LA_CASE;
  const facteur = Math.min(1, disponible / Math.max(1, largeurNaturelle));
  const largeur = Math.round(largeurNaturelle * facteur);
  const hauteur = Math.round(hauteurNaturelle * facteur);

  const etat = api.getAppState();
  // Le coin haut-gauche de ce qui est visible, en points de la grille.
  const hautVisible = -etat.scrollY;
  const contenu = api.getSceneElements().filter((e) => !estUneCase(e));
  const basDuContenu = contenu.reduce(
    (bas, e) => Math.max(bas, e.y + (e.height ?? 0)),
    0,
  );
  return {
    positionX: GRILLE_ORIGINE_X + MARGE_DANS_LA_CASE,
    // « À la suite du contenu existant » (Q363) — et jamais au-dessus de ce qu'on
    // regarde, pour que le dépôt se voie sans avoir à faire défiler.
    positionY: Math.max(hautVisible + MARGE_DANS_LA_CASE, basDuContenu + MARGE_DANS_LA_CASE),
    largeur,
    hauteur,
  };
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES OCTETS D'UNE IMAGE, RÉCUPÉRÉS SANS UN SEUL APPEL RÉSEAU ÉCRIT À LA MAIN.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Le dépôt interdit le `fetch` nu, et `web/e2e/port-du-canal.spec.ts` le vérifie par
 * balayage de source.** C'est la règle de `DOCTRINE.md` §11 — « contourner la couche
 * contrat en écrivant un appel réseau nu » —, et elle vaut ici même si l'adresse n'est
 * pas celle de l'API : une exception ouverte pour une image en ouvrirait d'autres.
 *
 * **Une balise image, elle, n'est pas un appel écrit à la main** : c'est le chemin que
 * `ImageDeContenu` emprunte déjà pour tout ce qui vient du domaine du contenu.
 *
 * **POURQUOI ON NE SE CONTENTE PAS DE L'ADRESSE, ET C'EST LE POINT LE PLUS FIN.** Le
 * moteur accepte une adresse distante comme source d'une image, et elle **s'affiche**.
 * Mais le document se fabrique en dessinant le SVG exporté dans un canevas, et
 * `document.ts::tramer` l'écrit depuis le premier jour : « **le canevas se salit si le
 * SVG référence une adresse externe**, et `toBlob` lève alors une erreur de sécurité au
 * lieu de rendre des octets. Le jour où une image de tableau arrivera par une adresse
 * signée (D-116), **c'est cette ligne qui rougira, loin de sa cause.** »
 *
 * **La réparation est ici, à la source, et non là-bas** : l'image est convertie en
 * source `data:` **avant** d'entrer dans la scène. Une source `data:` n'est pas une
 * adresse externe, donc le canevas ne se salit pas, donc le document sort.
 *
 * **CE QUE ÇA DEMANDE À L'INFRASTRUCTURE, ET IL FAUT LE LIRE.** Lire les pixels d'une
 * image venue d'une autre origine exige que le contenant réponde `Access-Control-Allow-Origin`.
 * Le contenant local le fait ; **celui de la recette et de la production doit être réglé
 * pour cela**, et c'est un geste d'infrastructure, pas du code — de la famille du
 * prérequis 6. **Sans lui, la conversion échoue, et le repli est l'adresse brute** :
 * l'image s'affiche, et **elle manque au document**. Le repli est nommé plutôt que
 * silencieux.
 */
async function sourceDeLImage(
  adresse: string,
): Promise<{ source: string; largeur: number; hauteur: number }> {
  const image = await new Promise<HTMLImageElement>((resoudre, rejeter) => {
    const balise = new Image();
    // Sans cet attribut, le canevas se salit même quand le contenant répond CORS.
    balise.crossOrigin = 'anonymous';
    balise.onload = () => resoudre(balise);
    balise.onerror = () =>
      rejeter(new Error('Cette image n’a pas pu être chargée.'));
    balise.src = adresse;
  });

  const largeur = image.naturalWidth;
  const hauteur = image.naturalHeight;
  try {
    const canevas = document.createElement('canvas');
    canevas.width = largeur;
    canevas.height = hauteur;
    const contexte = canevas.getContext('2d');
    if (contexte === null) return { source: adresse, largeur, hauteur };
    contexte.drawImage(image, 0, 0);
    // Lève `SecurityError` quand le contenant n'a pas répondu CORS : le repli est
    // l'adresse brute, et le document perdra cette image. Écart nommé ci-dessus.
    return { source: canevas.toDataURL('image/png'), largeur, hauteur };
  } catch {
    return { source: adresse, largeur, hauteur };
  }
}

/**
 * **Le poids d'un élément, estimé côté front.**
 *
 * **Le serveur fait autorité** : il rend `poidsCourant` à chaque écriture, et c'est lui
 * que la jauge montre. Cette estimation sert **entre deux écritures** : « une jauge
 * permanente est visible de tous pendant tout le cours », et attendre la réponse suivante
 * la ferait sauter par à-coups. Elle mesure **ce qui part réellement**, la sérialisation
 * JSON — le seul nombre qui a la même unité que le plafond.
 */
export function poidsEstime(element: ElementDeTableau): number {
  return new TextEncoder().encode(JSON.stringify(element)).length;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **UNE IMAGE POSÉE SUR LE TABLEAU — F-24, ET C'EST LE PORT QUI LA PEINT.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **L'identité d'une image est celle du SERVEUR, et c'est l'inverse d'un tracé.** Un
 * tracé naît dans le moteur, qui lui donne son identité, et la route d'écriture la
 * reçoit (D-121). Une image naît de la **route de dépôt** : c'est elle qui reçoit les
 * octets, les réduit, les range et rend la clé. Le moteur ne l'invente donc pas — le
 * produit la lui pose, avec son identité déjà faite.
 *
 * **L'adresse est SIGNÉE ET DE COURTE DURÉE**, servie par le domaine séparé sans
 * cookies (D-005). Elle borne le **chargement**, pas l'affichage : une fois les octets
 * dans la scène, l'expiration ne retire rien de l'écran.
 *
 * **Le placement est celui du modèle, et il peut être vide** sur une image posée avant
 * le 20260829 : elle se repose alors à la suite du contenu.
 */
export type ImageDeTableau = {
  readonly id: string;
  readonly adresse: string | null;
  readonly positionX: number | null;
  readonly positionY: number | null;
  readonly largeur: number | null;
  readonly hauteur: number | null;
};

/**
 * **Un pense-bête posé sur le tableau — F-22, Q352.**
 *
 * « Un rectangle **coloré** portant un **texte**, une **couleur**, son **auteur** et sa
 * **position**, et rien d'autre. » Comme l'image, il porte l'identité du serveur : il
 * est relu d'une scène, et non fabriqué par le moteur.
 */
export type PenseBeteDeTableau = {
  readonly id: string;
  readonly texte: string;
  readonly couleur: string | null;
  readonly positionX: number | null;
  readonly positionY: number | null;
};

/** Où un élément se pose sur la grille, en points A4. */
export type PlacementSurLaGrille = {
  readonly positionX: number;
  readonly positionY: number;
  readonly largeur: number;
  readonly hauteur: number;
};

/** Un participant dont le curseur se montre, nommé. DD-08. */
export type CurseurNomme = {
  readonly personneId: string;
  readonly prenom: string | null;
  readonly x: number;
  readonly y: number;
};

type Props = {
  /** La scène en place, telle que le serveur l'a rendue. */
  readonly elementsEnPlace: readonly ElementDeTableau[];
  /** Ce qui arrive des autres postes depuis l'ouverture. */
  readonly elementsRecus: readonly ElementDeTableau[];
  /** Les images de la scène en place. Le port les charge et les peint. */
  readonly imagesEnPlace?: readonly ImageDeTableau[];
  /** Les pense-bêtes de la scène en place. */
  readonly penseBetesEnPlace?: readonly PenseBeteDeTableau[];
  readonly outil: Outil;
  readonly encre: Encre;
  readonly epaisseur: number;
  /** Au plafond, « le cours est gelé en écriture, **pas même le tuteur ne trace** ». */
  readonly gele: boolean;
  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **LA LECTURE STRICTE, ET ELLE N'EST PAS LE GEL.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * `gele` porte **une seule règle** : « au plafond, le cours est gelé en écriture,
   * pas même le tuteur ne trace » (F-21). C'est un état **du tableau vivant**, qui se
   * lève en retirant du contenu.
   *
   * `lectureStricte` porte **deux autres règles, et elles n'ont rien à voir** : la
   * relecture d'une scène passée « s'ouvre dans la même surface que le tableau vivant,
   * **en lecture stricte** » (F-47, F-26), et l'observation d'un cours en direct est
   * « **en lecture stricte** : il n'écrit pas, ne parle pas, **ne produit aucun
   * curseur** » (Q216, Q401, DD-08).
   *
   * **Les confondre serait dire à quelqu'un qui relit une archive que son tableau est
   * plein.** Les deux ferment l'écriture du moteur ; **une seule ferme le pointeur**.
   */
  readonly lectureStricte?: boolean;
  readonly curseurs: readonly CurseurNomme[];
  /** Un élément vient d'être posé ici : à persister et à diffuser. */
  readonly onPose: (element: ElementDeTableau, poids: number) => void;
  /**
   * **Un élément DÉJÀ posé vient de changer ici : à persister — D-121, servi.**
   *
   * Sans ce rappel, « déplacer ou redimensionner un élément déjà annoncé ne se
   * conserve pas, il revient au rechargement là où il est né ». Il porte l'identité
   * telle qu'elle a été annoncée, et le contenu d'après le geste.
   */
  readonly onMiseAJour?: (
    identite: string,
    element: ElementDeTableau,
    poids: number,
  ) => void;
  /** Des éléments viennent d'être retirés ici : à persister. */
  readonly onRetrait: (identites: readonly string[]) => void;
  /** Le pointeur a bougé : à diffuser, à la cadence de l'appelant. */
  readonly onPointeur: (x: number, y: number, trace: boolean) => void;
  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **UN ÉLÉMENT VIENT D'ÊTRE DÉSIGNÉ — F-47, ET C'EST UNE OPÉRATION DE PLUS
   * SUR UNE INTERFACE QUE D-012 VEUT ÉTROITE.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * **Le geste servi.** F-47 ferme sa liste à quatre : « regarder, **désigner un tracé
   * pour voir son auteur**, circuler entre les tableaux, fabriquer le document ». Les
   * trois autres étaient servis, celui-ci ne l'était par **aucune surface** — seuls les
   * pense-bêtes avaient leur auteur lisible, et le commentaire de `PageRelecture` le
   * disait en toutes lettres. F-26 porte la règle : « la relecture montre **l'auteur de
   * chaque tracé**, à tout le monde qui relit » (Q376).
   *
   * ───────────────────────────────────────────────────────────────────────
   * **POURQUOI L'OPÉRATION EST NÉCESSAIRE, ET POURQUOI ELLE N'AMENDE PAS
   * D-012.**
   * ───────────────────────────────────────────────────────────────────────
   *
   * **Aucun écran ne peut désigner sans ce rappel.** L'auteur d'un tracé vit dans la
   * réponse de la scène, que la page a déjà ; ce qui lui manque est **lequel des
   * éléments la personne vient de toucher**. Cette question ne se répond qu'avec la vue
   * du moteur — sa position de défilement, son facteur d'agrandissement, l'ordre de sa
   * scène —, et **elle n'a de réponse nulle part ailleurs**. La seule autre voie serait
   * qu'un écran lise la scène du moteur lui-même, ce que le port existe pour empêcher.
   *
   * **CE QUE D-012 PROTÈGE EST INTACT.** Sa dernière phrase exige « une interface
   * étroite […] sinon le remplacement n'est plus un changement de moteur mais une
   * réécriture du front de séance ». La mesure de l'étroitesse n'est pas le **nombre**
   * d'opérations, c'est **ce qui traverse** : ce rappel rend **une chaîne**, l'identité
   * de l'élément, celle-là même que `identiteDe` rend déjà et que le contrat d'écriture
   * transporte depuis D-121. **Aucun type du moteur ne sort d'ici, aucune coordonnée,
   * aucune forme.** Le jour où Ziteboard remplace Excalidraw, cette opération se réécrit
   * dans ce fichier comme les autres, et aucun écran ne bouge.
   *
   * **LE PRÉCÉDENT EST DATÉ, ET IL A CONCLU DANS L'AUTRE SENS — C'EST POURQUOI L'ÉCART
   * SE JUSTIFIE ICI.** Sur le canal, le porteur a tranché qu'« une opération de plus sur
   * le canal, **donc elle touche D-002** » (D-120, puis D-127 pour la huitième). **Ce qui
   * rend ce cas différent tient en une phrase** : D-002 borne un **protocole**, dont la
   * liste des opérations est elle-même la décision — chaque message de plus est une chose
   * de plus qui traverse le réseau, que le serveur doit admettre et que le modèle de
   * convergence doit tenir. D-012 borne un **remplacement**, et ce qui le rend cher n'est
   * pas le nombre d'appels mais le nombre de fichiers à réécrire le jour venu. **Ce
   * nombre reste un.** `web/e2e/port-du-moteur.spec.ts` le tient, et il reste vert.
   *
   * **DONC : D-012 N'EST PAS AMENDÉE, ET LE MOTIF EST ÉCRIT PLUTÔT QUE SUPPOSÉ.** Le
   * jour où une opération de ce port ferait sortir une forme du moteur — un élément, un
   * état, une géométrie —, ce serait un amendement, et il se journaliserait.
   *
   * **Optionnel, et c'est la même règle que `onMiseAJour`** : la surface de tracé d'un
   * cours vivant ne le passe pas, donc ce chemin n'y coûte rien.
   *
   * Rend `null` quand l'appui n'a touché aucun élément — désigner le vide **efface** la
   * désignation, plutôt que de laisser à l'écran l'auteur d'un tracé qu'on ne montre plus.
   */
  readonly onDesignation?: (identite: string | null) => void;
  /** L'API du moteur, remontée pour les gestes ponctuels et l'export. */
  readonly onPret: (commandes: CommandesDuTableau) => void;
};

/**
 * **Les gestes ponctuels et l'export, remontés à l'écran — et ils ne fuient rien.**
 *
 * Ce sont des **fonctions**, jamais l'API du moteur : c'est ce qui permet à la barre
 * d'outils et à l'écran de les déclencher sans nommer la bibliothèque, comme les
 * vignettes de la visio reçoivent `brancher` plutôt qu'une piste.
 */
export type CommandesDuTableau = {
  /** « Annuler et refaire » — F-22. Local à son auteur, et c'est la règle Q349. */
  readonly annuler: () => void;
  readonly refaire: () => void;
  /** « Tout effacer », exercé par n'importe quel présent (Q348). */
  readonly toutEffacer: () => void;
  /** Poser un pense-bête au centre de la vue — objet métier, Q352. */
  readonly poserUnPenseBete: (texte: string, couleur: string) => void;
  /**
   * **Où une image de ces proportions se poserait, au centre de la vue.**
   *
   * L'écran a besoin de ces quatre nombres **avant** d'envoyer les octets, la route de
   * dépôt les recevant avec eux : sans cela, l'image reviendrait au rechargement à une
   * place que personne n'a choisie. Le port les calcule parce que **lui seul connaît la
   * grille** — « l'origine de la grille est une constante du produit » (Q342), et
   * `web/e2e/port-du-moteur.spec.ts` refuse qu'un autre fichier la nomme.
   */
  readonly placerUneImage: (
    largeurNaturelle: number,
    hauteurNaturelle: number,
  ) => PlacementSurLaGrille;
  /**
   * **Poser sur la scène une image que le serveur vient d'accepter — F-24.**
   *
   * Elle porte **l'identité du serveur**, et le port l'enregistre comme déjà annoncée :
   * elle ne repartira donc jamais par la route d'écriture, qui ne connaît que les
   * tracés et les pense-bêtes. La gomme, elle, la retire par cette même identité.
   */
  readonly poserUneImage: (image: ImageDeTableau) => Promise<void>;
  /**
   * **Les pages non vides, en SVG, dans l'ordre du haut vers le bas** — F-25 et D-001.
   *
   * « Tout part, toujours : toutes les cases **non vides**, aucun choix possible »
   * (Q371), et « les cases vides sont écartées : le document n'a jamais de page
   * blanche » (Q341).
   */
  readonly exporterLesPages: () => Promise<readonly PageExportee[]>;
};

export type PageExportee = {
  readonly rang: number;
  readonly svg: string;
  readonly largeur: number;
  readonly hauteur: number;
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA SURFACE DE TRACÉ.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `DESIGN.md` : « **une durée à zéro sur la surface de tracé**, et c'est une valeur, pas
 * un oubli : aucune transition, aucune animation, aucune ombre mouvante au-dessus du
 * tracé, **qui partage la machine avec l'encodage vidéo** ». D-004 borne la visio pour la
 * même raison : « la latence du trait se mesure toujours avec la visio allumée ».
 *
 * **L'INTERFACE NATIVE DU MOTEUR EST MASQUÉE, ET C'EST D-001** : « l'interface publique
 * d'excalidraw.com n'est pas la référence : le produit expose **sa propre** barre d'outils
 * et ses propres actions, construites sur l'API du paquet. **L'interface d'export native
 * est masquée.** » Tout ce que `UIOptions` peut fermer est fermé, le menu principal est
 * vidé, et **c'est aussi ce qui rend inatteignable le dialogue Mermaid** — dont la chaîne
 * de dépendances porte deux avis d'audit, voir D-115.
 */
export function SurfaceDeTrace({
  elementsEnPlace,
  elementsRecus,
  imagesEnPlace,
  penseBetesEnPlace,
  outil,
  encre,
  epaisseur,
  gele,
  lectureStricte = false,
  curseurs,
  onPose,
  onMiseAJour,
  onRetrait,
  onPointeur,
  onDesignation,
  onPret,
}: Props) {
  const [api, poserLApi] = useState<ExcalidrawImperativeAPI | null>(null);
  /** Ce que ce poste a déjà annoncé : sans lui, chaque frappe rejouerait toute la scène. */
  const annonces = useRef(new Map<string, number>());
  /** Ce dont la version a bougé et qui n'est pas encore fini : voir `surChangement`. */
  const enAttente = useRef(new Map<string, number>());
  const minuterie = useRef<number | null>(null);

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **LA RETENUE DE L'API EST DIFFÉRÉE D'UNE MICRO-TÂCHE, ET C'EST CE QUI
   * FAIT QUE LE TABLEAU DESSINE.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * **`excalidrawAPI` est appelée dans le CONSTRUCTEUR de l'App du moteur**, lu dans la
   * source du paquet en 0.18.1 — pas dans son montage. Lui passer un poseur de `useState`
   * en direct est donc **un `setState` pendant le rendu d'un autre composant**, et
   * l'effet qui en découle s'exécute trop tôt : `setActiveTool` y appelle un `setState`
   * du moteur **qui est perdu**. Aucun outil ne s'arme, donc rien ne se dessine — c'est le
   * blocage que D-115 a laissé ouvert, et il n'était **ni dans `UIOptions`, ni dans le
   * mode zen, ni dans l'API**.
   *
   * **Mesuré au navigateur le 20260828, les deux formes côte à côte dans la même page,
   * sous `StrictMode`, avec les props réelles de ce port :**
   *
   * | retenue | outil après l'effet | tracé à la souris |
   * |---|---|---|
   * | en direct | `selection` | **0 élément** |
   * | différée d'une micro-tâche | **`freedraw`** | **1 élément** |
   *
   * **Et le moteur construit son App DEUX FOIS sous `StrictMode`** : la première n'a
   * jamais mesuré son conteneur — `getAppState()` y rend les dimensions de la fenêtre et
   * non celles de la boîte —, et tout appel sur elle est perdu. Différer fait que la
   * dernière remontée gagne, et c'est la vivante.
   *
   * **Ne pas « simplifier » en `excalidrawAPI={poserLApi}`** : la forme est plus courte,
   * elle compile, elle monte, la grille paraît — et le tableau ne dessine plus.
   * `web/e2e/port-du-moteur.spec.ts` le tient.
   */
  const retenirLApi = useCallback((instance: ExcalidrawImperativeAPI) => {
    queueMicrotask(() => poserLApi(instance));
  }, []);

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **LES RAPPELS PASSENT PAR UNE RÉFÉRENCE, ET SANS CELA LE MOTEUR BOUCLE.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * **Constaté au navigateur le 20260827** : `Maximum update depth exceeded`, et l'écran
   * blanc. La cause est une chaîne courte et vicieuse — l'effet qui remonte les commandes
   * appelle `onPret`, qui est un `setState` chez l'appelant ; le rendu qui suit recrée les
   * rappels **passés en flèche depuis la page**, donc leur identité change, donc l'effet
   * se rejoue, donc `onPret` rappelle. Une boucle sans fin, et **rien dans le typage ne la
   * signale**.
   *
   * **La réparation est ici et pas chez l'appelant**, et c'est un choix : exiger que
   * chaque écran enveloppe six rappels dans `useCallback` est une discipline qui sera
   * appliquée au hasard — « une règle qui repose sur la discipline humaine sera appliquée
   * au hasard » (`DOCTRINE.md` §0). Le port lit donc les rappels **dans une référence
   * tenue à jour**, et ses effets ne dépendent plus de leur identité.
   */
  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **LA SCÈNE SE SÈME PAR `initialData`, ET `updateScene` NE SUFFIT PAS —
   * C'EST LA CAUSE QUI A DEMANDÉ SIX MESURES.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * **Le symptôme** : la grille était poussée par `updateScene`, `getSceneElements()`
   * en rendait bien trois **juste après**, et **zéro une seconde plus tard**. Rien ne se
   * peignait, et le tracé à la souris ne créait aucun élément.
   *
   * **Cinq causes écartées par la mesure, chacune en isolant** : deux copies de React,
   * corrigée par `resolve.dedupe` ; un `updateScene` sans ses éléments ; un compteur de
   * cases en référence qui survit au remontage de `StrictMode` ; la discipline de capture
   * `captureUpdate` ; le mode collaboration. **Aucune n'était celle-là**, et la dernière
   * mesure a été faite **tous les autres effets coupés** — la scène se vidait quand même.
   *
   * **La cause : `updateScene` sur une scène que rien n'a INITIALISÉE ne tient pas.** Le
   * moteur établit sa scène depuis `initialData`, au montage, et `updateScene` la
   * **mute** ensuite. Pousser dans une scène jamais établie est accepté pour un instant
   * puis jeté.
   *
   * **La grille est donc le SEMIS**, ce qui tombe juste : F-23 la veut « tracée
   * **d'emblée**, visible en permanence », et elle est « une constante du produit ». Ce
   * qui arrive après — la scène en place, ce qui vient des autres — passe par
   * `updateScene`, sur une scène qui existe.
   *
   * **`useMemo` et non un appel direct** : `initialData` n'est lu qu'au montage, mais le
   * recalculer à chaque rendu fabriquerait trois objets par frappe pour rien.
   */
  const semis = useMemo(
    () => ({ elements: grille(CASES_AU_DEPART), scrollToContent: false }),
    [],
  );

  const rappels = useRef({
    onPose,
    onMiseAJour,
    onRetrait,
    onPointeur,
    onDesignation,
    onPret,
  });
  /**
   * **Où l'appui a commencé, pour distinguer une désignation d'un déplacement de la vue.**
   * En mode lecture, tout appui sur le canevas déplace la vue : sans cette comparaison,
   * relâcher au bout d'un déplacement désignerait ce qui se trouve sous le doigt.
   */
  const departDuGeste = useRef<{ x: number; y: number } | null>(null);
  /**
   * **La mise à jour se fait dans un effet, pas pendant le rendu**, et c'est une seconde
   * barrière de lint qui l'a imposé : « accéder à une référence pendant le rendu peut
   * empêcher le composant de se mettre à jour ». Elle a raison, et cela ne change rien
   * ici — ces rappels ne sont jamais appelés pendant un rendu, seulement depuis un
   * événement du moteur ou depuis un effet, tous deux postérieurs.
   */
  useEffect(() => {
    rappels.current = {
      onPose,
      onMiseAJour,
      onRetrait,
      onPointeur,
      onDesignation,
      onPret,
    };
  }, [onPose, onMiseAJour, onRetrait, onPointeur, onDesignation, onPret]);

  /**
   * **La grille naît à l'ouverture et s'étend sous le contenu** — F-23, « infinie vers le
   * bas ». Elle est recalculée, jamais reçue : deux postes ont la même sans rien échanger.
   */
  const tenirLaGrille = useCallback(
    (moteur: ExcalidrawImperativeAPI, elements: readonly ExcalidrawElement[]) => {
      const contenu = elements.filter((e) => !estUneCase(e));
      const basDuContenu = contenu.reduce(
        (bas, e) => Math.max(bas, e.y + (e.height ?? 0)),
        0,
      );
      const voulu = Math.max(
        CASES_AU_DEPART,
        rangDeLOrdonnee(basDuContenu) + CASES_DE_MARGE,
      );

      /**
       * ═══════════════════════════════════════════════════════════════════════
       * **LE COMPTE SE LIT DANS LA SCÈNE, ET UN COMPTEUR À CÔTÉ A COÛTÉ UN ÉCRAN
       * BLANC.**
       * ═══════════════════════════════════════════════════════════════════════
       *
       * Cette fonction gardait le nombre de cases posées dans une **référence**, et
       * sortait tôt quand il suffisait. **Le tableau restait blanc, et la cause a demandé
       * trois mesures** : `StrictMode` monte, démonte et remonte en développement ; la
       * référence **survit** à ce cycle, la scène du moteur **non**. Au second montage, le
       * compteur disait « trois cases posées » et le moteur en avait zéro — **et rien ne
       * les reposait jamais.**
       *
       * **C'est la forme de D-110** : « une valeur employée comme preuve d'un état qu'elle
       * ne mesure pas ». La réparation est de lire l'état lui-même — le compte de cases
       * **dans la scène** —, ce qui rend cette fonction idempotente et vraie après
       * n'importe quel remontage.
       *
       * **Et ce n'est pas un défaut de développement seulement** : le moteur remet sa
       * scène à zéro à chaque remontage, en production comme ailleurs. Un compteur à côté
       * de la scène aurait fini par mentir de la même façon.
       */
      const casesEnPlace = elements.filter(estUneCase).length;
      if (voulu <= casesEnPlace) return;
      const sansCases = moteur.getSceneElements().filter((e) => !estUneCase(e));
      moteur.updateScene({
        elements: [...grille(voulu), ...sansCases],
        // **Sans cette ligne, la grille disparaît de la scène.** Voir l'en-tête.
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    },
    [],
  );

  /**
   * **La grille naît avec le moteur, et non à l'arrivée d'un élément.**
   *
   * **Constaté au navigateur le 20260827 : le tableau était blanc.** Sa création vivait
   * dans l'effet de chargement, qui **sort tôt quand rien de neuf n'arrive** — et sur un
   * tableau vierge, rien n'arrive jamais. La grille de F-23 est « tracée **d'emblée**,
   * visible en permanence » : elle ne peut donc pas dépendre du contenu, elle le précède.
   */
  useEffect(() => {
    if (api === null) return;
    tenirLaGrille(api, api.getSceneElements());
  }, [api, tenirLaGrille, elementsEnPlace, elementsRecus]);

  // ── `charger` : la scène en place, plus ce qui est arrivé des autres ──
  useEffect(() => {
    if (api === null) return;
    const recus = [...elementsEnPlace, ...elementsRecus] as unknown as ExcalidrawElement[];
    // **Ce que ce poste a posé lui-même ne se réécrit pas** : le moteur en est
    // l'autorité locale, et le remplacer sous le stylet ferait sauter le trait.
    const dejaLa = new Set(
      api.getSceneElements().map((e) => (e as ExcalidrawElement).id),
    );
    const nouveaux = recus.filter((e) => !dejaLa.has(e.id));
    if (nouveaux.length === 0) return;
    for (const element of nouveaux) annonces.current.set(element.id, element.version);
    const scene = [...api.getSceneElements(), ...nouveaux] as ExcalidrawElement[];
    api.updateScene({ elements: scene });
    tenirLaGrille(api, scene);
  }, [api, elementsEnPlace, elementsRecus, tenirLaGrille]);

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **LES IMAGES DE LA SCÈNE, PEINTES PAR LE PORT — F-24, D-116 fermée.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * D-116 avait arrêté le dépôt sur trois manques, dont le troisième : « **le canevas
   * ne peint que des tracés** — `ElementPose` ne connaît qu'une `Trace`, et
   * `scene.data.images` n'est lu par personne ». Les deux premiers étaient du contrat,
   * et ils sont comblés : l'adresse signée arrive avec la scène.
   *
   * **L'identité de l'élément EST celle du serveur**, et elle est enregistrée comme
   * **déjà annoncée** : une image ne repart donc jamais par la route d'écriture, qui ne
   * connaît que les tracés et les pense-bêtes. La gomme, elle, la retire par cette même
   * identité — la route de retrait accepte les deux.
   *
   * **Une image sans placement se pose à la suite du contenu** : le modèle ne portait
   * aucune position avant le 20260829, et une ligne d'avant n'en a pas.
   *
   * **Une image sans adresse ne se pose pas du tout** : le stockage n'est configuré dans
   * aucun environnement (prérequis 6), et la scène est rendue sans elle plutôt que
   * refusée.
   */
  useEffect(() => {
    if (api === null || imagesEnPlace === undefined) return;
    let vivant = true;
    void (async () => {
      for (const image of imagesEnPlace) {
        if (!vivant || api === null) return;
        if (image.adresse === null) continue;
        if (annonces.current.has(image.id)) continue;
        // Marquée AVANT le chargement : l'effet se rejoue au moindre rendu, et deux
        // chargements de la même image poseraient deux éléments.
        annonces.current.set(image.id, 0);
        try {
          await poserLImageSurLaScene(api, image);
        } catch {
          // Une image qui ne se charge pas ne casse pas la scène. Elle sera retentée
          // au prochain montage : son identité repart avec le registre d'annonces.
          annonces.current.delete(image.id);
        }
      }
    })();
    return () => {
      vivant = false;
    };
  }, [api, imagesEnPlace]);

  /**
   * **LES PENSE-BÊTES DE LA SCÈNE — F-22, Q352.**
   *
   * Même mécanique que les images, et même motif : ils portent l'identité du serveur,
   * ils sont marqués déjà annoncés, et la gomme les retire par elle. **Leur couleur et
   * leur position viennent du modèle depuis le 20260829** ; avant, le contrat les
   * recevait et rien ne les écrivait, donc une ligne d'avant revient sans couleur, à la
   * suite du contenu.
   */
  useEffect(() => {
    if (api === null || penseBetesEnPlace === undefined) return;
    for (const note of penseBetesEnPlace) {
      if (annonces.current.has(note.id)) continue;
      annonces.current.set(note.id, 0);
      const place = note.positionX !== null && note.positionY !== null;
      const etat = api.getAppState();
      const fabriques = convertToExcalidrawElements([
        {
          id: note.id,
          type: 'rectangle',
          x: place ? (note.positionX ?? 0) : -etat.scrollX + 120,
          y: place ? (note.positionY ?? 0) : -etat.scrollY + 120,
          width: 220,
          height: 160,
          backgroundColor: note.couleur ?? 'transparent',
          fillStyle: 'solid',
          label: { text: note.texte, fontSize: 16 },
        } as unknown as Parameters<typeof convertToExcalidrawElements>[0] extends
          | readonly (infer S)[]
          | null
          ? S
          : never,
      ]);
      api.updateScene({
        elements: [...api.getSceneElements(), ...fabriques],
      });
    }
  }, [api, penseBetesEnPlace]);

  // ── les curseurs nommés des autres — DD-07, DD-08 ──
  useEffect(() => {
    if (api === null) return;
    /**
     * **Le moteur porte les curseurs de collaboration, et c'est DD-08 servi tel quel** :
     * « le curseur porte **le prénom et une couleur propre à la personne pour la durée du
     * cours**, et rien de plus ». **Rien d'autre n'est passé** — ni état de liaison, ni
     * qualité : DD-07 interdit « tout voyant sur qui que ce soit ».
     */
    // La clé est marquée `SocketId` par le moteur. Notre identifiant de personne y
    // entre tel quel : le moteur ne s'en sert que comme clé d'unicité.
    const collaborateurs = new Map(
      curseurs.map((c) => [
        c.personneId as unknown as Parameters<
          NonNullable<Parameters<ExcalidrawImperativeAPI['updateScene']>[0]['collaborators']>['get']
        >[0],
        {
          username: c.prenom ?? '',
          pointer: { x: c.x, y: c.y, tool: 'pointer' as const },
        },
      ]),
    );
    // **Aucun `elements` ici, et c'est l'inverse de `reglerLEtat`** : cet effet se
    // rejoue à CHAQUE mouvement de souris d'un autre poste, donc lui passer un
    // instantané des éléments remplacerait la scène en pleine frappe — le trait qu'on
    // est en train de poser serait écrasé par l'état d'avant.
    api.updateScene({ collaborators: collaborateurs });
  }, [api, curseurs]);

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **L'OUTIL ET LES DEUX RÉGLAGES EN UN SEUL EFFET, ET L'ORDRE EST LE
   * CORRECTIF.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * **Constaté au navigateur** : la barre annonçait « Crayon » et le moteur était en
   * `selection` — donc dessiner à la souris ne traçait rien, cela sélectionnait.
   *
   * **CE QUE CE COMMENTAIRE AFFIRMAIT DE LA CAUSE EST FAUX, ET LA SOURCE DU PAQUET LE
   * DÉMENT.** Il écrivait que « `updateScene({ appState })` remet l'outil actif à
   * `selection` », et que `updateScene({ collaborators })` fait de même. **Ni l'un ni
   * l'autre ne touche `activeTool`** : lu dans `dist/dev` en 0.18.1, `updateScene` fait
   * `if (sceneData.appState) this.setState(sceneData.appState)` — un `setState`
   * **partiel**, qui ne porte que les clés données — et
   * `if (sceneData.collaborators) this.setState({ collaborators })`. La vraie cause du
   * symptôme était la retenue de l'api, en tête de ce composant.
   *
   * **CE QUI RESTE VRAI, ET POURQUOI LA FORME NE CHANGE PAS.** Un seul effet, réglage
   * avant outil, déclaré après celui des curseurs : cette forme est **correcte** et elle
   * ne coûte rien. Elle n'est simplement plus une **nécessité**, et personne ne doit la
   * défendre au nom d'un mécanisme qui n'existe pas. La démonter demanderait de
   * rééprouver l'armement au navigateur ; ce n'est pas gratuit, donc ce n'est pas fait
   * ici — mais ce n'est plus interdit.
   *
   * **La leçon générale, et c'est elle qu'il faut garder** : sur cette API, une
   * explication qui n'a pas été lue dans la source est une hypothèse. Les deux
   * affirmations ci-dessus ont tenu une journée entière, ont façonné ce fichier, et
   * étaient fausses.
   */
  useEffect(() => {
    if (api === null) return;

    // **Les couleurs viennent de `tokens.css`, jamais d'ici.** On lit la variable
    // calculée : c'est la seule façon de ne pas recopier une valeur de charte.
    const style = getComputedStyle(document.documentElement);
    const couleur = style.getPropertyValue(`--encre-${encre}`).trim();
    reglerLEtat(api, {
      currentItemStrokeColor: couleur || '#000000',
      currentItemStrokeWidth: epaisseur,
      // « Le surligneur » est le crayon translucide de la charte.
      currentItemOpacity: encre === 'surligneur' ? 50 : 100,
    });

    if (outil === 'pense-bete') {
      // Le pense-bête n'est pas un outil du moteur : on reste en sélection, et
      // l'écran pose l'objet par la commande du port.
      api.setActiveTool({ type: 'selection' });
      return;
    }
    const nom = outil === 'surligneur' ? 'freedraw' : OUTIL_DU_MOTEUR[outil];
    /**
     * **CET APPEL A ÉTÉ CRU INOPÉRANT, ET IL NE L'EST PAS — D-115 est levée.**
     *
     * Ce que D-115 mesurait était juste : l'outil restait `selection` immédiatement après
     * l'appel et une seconde plus tard. **Ce qu'elle en concluait était faux** : la cause
     * n'était ni `setActiveTool`, ni `UIOptions`, ni le mode zen, ni l'îlot natif masqué —
     * **c'était l'instant de l'appel**, et donc la façon dont ce fichier retenait l'api.
     * Voir la retenue différée en tête de ce composant.
     *
     * **Vérifié sur un `<Excalidraw>` NU, sans une seule prop** : le symptôme s'y
     * reproduit à l'identique. Ce qui écarte définitivement les trois chemins que D-115
     * laissait ouverts — et **l'îlot natif n'a pas été démasqué**, donc D-001 tient.
     */
    api.setActiveTool({ type: nom });
  }, [api, outil, encre, epaisseur]);

  // ── les commandes ponctuelles et l'export, remontées une fois ──
  useEffect(() => {
    if (api === null) return;
    rappels.current.onPret({
      annuler: () => clavier(api, 'z', { ctrl: true }),
      refaire: () => clavier(api, 'z', { ctrl: true, maj: true }),
      toutEffacer: () => {
        // « N'importe quel présent exerce le tout effacer » (Q348), « tout le monde
        // efface le tracé de tout le monde » (Q350) — **et l'annulation ne le défait
        // que pour son auteur** (Q349), ce que l'historique local du moteur tient déjà.
        const aRetirer = api
          .getSceneElements()
          .filter((e) => !estUneCase(e))
          .map((e) => e.id);
        if (aRetirer.length === 0) return;
        api.updateScene({
          elements: api.getSceneElements().filter(estUneCase),
        });
        rappels.current.onRetrait(aRetirer);
      },
      poserUnPenseBete: (texte, couleur) => {
        const etat = api.getAppState();
        const fabriques = convertToExcalidrawElements([
          {
            type: 'rectangle',
            x: -etat.scrollX + 120,
            y: -etat.scrollY + 120,
            width: 220,
            height: 160,
            backgroundColor: couleur,
            fillStyle: 'solid',
            label: { text: texte, fontSize: 16 },
          },
        ]);
        api.updateScene({
          elements: [...api.getSceneElements(), ...fabriques],
        });
      },
      placerUneImage: (largeurNaturelle, hauteurNaturelle) =>
        placementDansLaVue(api, largeurNaturelle, hauteurNaturelle),
      poserUneImage: async (image) => {
        annonces.current.set(image.id, 0);
        await poserLImageSurLaScene(api, image);
      },
      exporterLesPages: () => exporterLesPages(api),
    });
    // **Ne dépend que du moteur** : les rappels passent par la référence ci-dessus, et
    // les mettre ici est exactement ce qui faisait boucler.
  }, [api]);

  /**
   * ═══════════════════════════════════════════════════════════════════════════
   * **CE QUI A CHANGÉ ICI PART AU SERVEUR ET AUX AUTRES — UNE FOIS PAR ÉLÉMENT
   * FINI, ET NON UNE FOIS PAR VERSION.**
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * **Ce commentaire affirmait le contraire, et la mesure l'a démenti.** Il écrivait que
   * n'annoncer qu'un élément « dont la version a bougé » évitait « des centaines
   * d'envois ». **Ça n'évite rien** : le moteur incrémente la version de l'élément à
   * chaque point ajouté.
   *
   * **Mesuré au navigateur le 20260828, un seul trait de quarante mouvements de
   * souris : 42 versions distinctes, de la 2 à la 43.** Sans cette réparation, ce trait
   * produirait **42 diffusions et 42 écritures**, donc **42 lignes `Stroke` pour un
   * trait** — le plafond de messages de R5 en jetterait la plus grande part, le poids du
   * tableau serait compté quarante-deux fois, et la gomme n'aurait aucune ligne à
   * retirer qui corresponde à ce qu'on voit.
   *
   * **La réparation : on met en attente, et on n'annonce qu'à la levée du stylet.** C'est
   * exactement ce que faisait la surface écrite à la main, dont le rappel s'appelait
   * `onTraceFini` — donc le produit ne change pas de comportement, il le retrouve.
   *
   * **Et une minuterie de secours double la levée de stylet**, parce que tout ce qui
   * entre en scène ne finit pas par un pointeur relâché sur le canevas : un pense-bête
   * posé par un bouton de la barre d'outils, un texte tapé puis quitté au clavier. Sans
   * elle, ces éléments-là resteraient en attente **sans jamais partir**.
   *
   * **Un retrait, lui, ne passe pas par l'attente** : il est fini par nature.
   */
  /**
   * **L'annonce de ce qui est fini, et elle est la seule à appeler `onPose`.**
   *
   * Elle relit l'élément **dans la scène** plutôt que de garder celui qu'`onChange` avait
   * passé : entre la mise en attente et la levée du stylet, le trait a continué de
   * grandir, et c'est sa forme finale qui doit partir. Un élément disparu ou retiré
   * pendant l'attente ne part pas.
   */
  const annoncerLAttente = useCallback(() => {
    if (minuterie.current !== null) {
      clearTimeout(minuterie.current);
      minuterie.current = null;
    }
    if (api === null || enAttente.current.size === 0) return;
    const parIdentite = new Map(api.getSceneElements().map((e) => [e.id, e]));
    for (const identite of enAttente.current.keys()) {
      const element = parIdentite.get(identite);
      if (element === undefined || element.isDeleted) continue;
      // **UNE SEULE ANNONCE PAR IDENTITÉ, ET C'EST LE CONTRAT QUI L'IMPOSE.** La route
      // d'écriture **crée** un élément ; il n'existe aucune route qui en mette un à jour.
      // Annoncer deux fois le même élément créerait donc **deux lignes en base** pour une
      // seule chose à l'écran : le poids serait compté deux fois, et la gomme n'en
      // retirerait qu'une.
      //
      // **CE QUE LA REMONTÉE DE D-121 A OUVERT EST DÉSORMAIS SERVI.** « Déplacer ou
      // redimensionner un élément déjà annoncé ne se conserve pas, il revient au
      // rechargement là où il est né » — parce qu'« aucune route ne met à jour ». La
      // route existe depuis le 20260829 : un élément déjà annoncé dont la version a
      // bougé part donc en **mise à jour**, et non en seconde création.
      //
      // **La condition est la version, pas la simple présence** : sans elle, un élément
      // que la scène rejoue à l'identique repartirait à chaque rendu.
      const dejaAnnoncee = annonces.current.get(identite);
      const opaque = element as unknown as ElementDeTableau;
      if (dejaAnnoncee !== undefined) {
        if (dejaAnnoncee === element.version) continue;
        annonces.current.set(identite, element.version);
        rappels.current.onMiseAJour?.(identite, opaque, poidsEstime(opaque));
        continue;
      }
      annonces.current.set(identite, element.version);
      rappels.current.onPose(opaque, poidsEstime(opaque));
    }
    enAttente.current.clear();
  }, [api]);

  /**
   * **La minuterie de secours se RÉARME à chaque point, et ce n'est pas un détail.**
   *
   * Armée une fois pour toutes, elle tombe **au milieu du trait** : mesuré, un trait de
   * quarante mouvements produisait alors **trois annonces** au lieu d'une — une par
   * échéance traversée, plus celle de la levée du stylet. Réarmée, elle ne tombe jamais
   * tant que la main écrit, et c'est la levée du stylet qui annonce.
   */
  const programmerLAnnonce = useCallback(
    (delai: number) => {
      if (minuterie.current !== null) clearTimeout(minuterie.current);
      minuterie.current = window.setTimeout(() => {
        minuterie.current = null;
        annoncerLAttente();
      }, delai);
    },
    [annoncerLAttente],
  );

  /**
   * **La levée du stylet est ce qui dit « c'est fini », et rien d'autre ne le dit.**
   *
   * `onPointerUp` est une souscription du moteur : elle rend sa fonction de retrait, et
   * ne pas l'appeler laisserait un abonné de plus à chaque remontage — dont `StrictMode`
   * fait un cas courant, pas un cas rare.
   */
  useEffect(() => {
    if (api === null) return;
    return api.onPointerUp(() => programmerLAnnonce(DELAI_APRES_LEVEE_MS));
  }, [api, programmerLAnnonce]);

  const surChangement = useCallback(
    (elements: readonly ExcalidrawElement[]) => {
      for (const element of elements) {
        if (estUneCase(element)) continue;
        if (element.isDeleted) {
          // **Un retrait est un acte fini**, lui : il part tout de suite. Et il purge
          // l'attente, sans quoi on annoncerait la pose d'un élément déjà retiré.
          enAttente.current.delete(element.id);
          if (annonces.current.delete(element.id)) {
            rappels.current.onRetrait([element.id]);
          }
          continue;
        }
        const vue = annonces.current.get(element.id);
        if (vue === element.version) continue;
        enAttente.current.set(element.id, element.version);
        programmerLAnnonce(DELAI_DE_SECOURS_MS);
      }
    },
    [programmerLAnnonce],
  );

  return (
    <div
      className="surface-de-trace relative flex-1 overflow-hidden rounded-4 border border-tableau-bord-page"
      /*
        **LA DÉSIGNATION SE LIT SUR LE CONTENEUR, ET NON SUR UN RAPPEL DU MOTEUR.**

        Voir la prop `onDesignation` pour le motif, et `elementDesigneA` pour les deux
        mécanismes du moteur qui ferment les autres voies en mode lecture. Ces deux
        rappels ne font rien tant qu'aucun écran ne demande la désignation.
      */
      onPointerDown={(evenement) => {
        departDuGeste.current = { x: evenement.clientX, y: evenement.clientY };
      }}
      onPointerUp={(evenement) => {
        const depart = departDuGeste.current;
        departDuGeste.current = null;
        const designer = rappels.current.onDesignation;
        if (api === null || designer === undefined || depart === null) return;
        if (
          Math.abs(evenement.clientX - depart.x) > TOLERANCE_DU_GESTE ||
          Math.abs(evenement.clientY - depart.y) > TOLERANCE_DU_GESTE
        ) {
          return;
        }
        designer(elementDesigneA(api, depart.x, depart.y));
      }}
    >
      {/*
        ═══════════════════════════════════════════════════════════════════════
        **L'INTERFACE NATIVE DU MOTEUR EST MASQUÉE ICI, ET C'EST D-001.**
        ═══════════════════════════════════════════════════════════════════════

        « L'interface publique d'excalidraw.com n'est pas la référence : le produit expose
        **sa propre** barre d'outils et ses propres actions, construites sur l'API du
        paquet. **L'interface d'export native est masquée.** »

        **`zenModeEnabled` et `UIOptions` n'y suffisent pas, constaté au navigateur le
        20260827** : le premier retire les panneaux latéraux, le second ferme les actions
        de canevas une par une — et **l'îlot d'outils et le menu principal restaient à
        l'écran**, au-dessus de notre propre barre. Deux barres d'outils pour un tableau
        dont `DESIGN.md` veut la densité « minimale, presque nulle ».

        **Le masque vit DANS le port, et pas dans `styles.css`**, et c'est le même motif
        que le reste de ce fichier : ces sélecteurs sont de la connaissance du moteur. Les
        écrire dans la feuille de style globale ferait un second endroit à corriger le jour
        où Ziteboard remplace Excalidraw — exactement ce que D-012 refuse.

        **Le zoom et le mode main RESTENT**, eux : « ils sont acquis par D-007 », donc le
        moteur les fournit et le produit n'a pas à les reproduire.
      */}
      <style>{MASQUE_DE_L_INTERFACE_NATIVE}</style>
      <Excalidraw
        excalidrawAPI={retenirLApi}
        initialData={semis}
        // `isCollaborating` est ce qui fait peindre les curseurs des autres.
        isCollaborating
        // **Le mode zen retire les panneaux du moteur**, dont ses propres boutons
        // d'annulation en bas à gauche : la barre d'outils du produit est la seule,
        // D-001. Le masque de style ci-dessous ferme ce qu'il laisse.
        zenModeEnabled
        onChange={surChangement}
        onPointerUpdate={({ pointer, button }) => {
          // **EN LECTURE STRICTE, AUCUN POINTEUR NE PART, ET C'EST DEUX RÈGLES.**
          // « Deux personnes qui relisent la même scène **ne se voient pas** : aucun
          // curseur, aucun prénom, aucun compte de lecteurs. **La relecture ne diffuse
          // rien** » (DD-12). Et une personne « partie regarder un tableau précédent
          // pendant le cours **n'a plus de curseur sur le tableau vivant, et n'en a pas
          // sur celui qu'elle regarde** » (règle 14 du domaine 4, DD-08) — elle **reste
          // présente** dans la liste, la présence étant celle du cours et non celle
          // d'un tableau. Enfin, un observateur « **ne produit aucun curseur** »
          // (Q401), « sinon l'invisibilité de l'observation du compte qui voit tout
          // tomberait par ce seul chemin ».
          //
          // **Le refus est ici et non chez l'appelant** : trois écrans doivent le
          // tenir, et une règle qu'on redemande à chacun est appliquée au hasard.
          if (lectureStricte) return;
          rappels.current.onPointeur(pointer.x, pointer.y, button === 'down');
        }}
        // **Deux règles, une seule prop du moteur** : le gel du plafond (F-21) et la
        // lecture stricte de la relecture et de l'observation (F-47, Q216). Voir la
        // prop `lectureStricte` pour ce qui les distingue.
        viewModeEnabled={gele || lectureStricte}
        // « Le zoom et le mode main sont acquis par D-007 » : ils restent au moteur.
        UIOptions={{
          canvasActions: {
            changeViewBackgroundColor: false,
            clearCanvas: false,
            export: false,
            loadScene: false,
            saveToActiveFile: false,
            saveAsImage: false,
            toggleTheme: false,
          },
          tools: { image: false },
        }}
      />
    </div>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **UN `updateScene` SANS SES ÉLÉMENTS VIDE LE TABLEAU — ET C'EST CE QUI A
 * FAIT UN ÉCRAN BLANC.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Mesuré au navigateur le 20260827, et le chemin est net** : la grille était bien
 * créée — trois cases, aux bonnes positions, avec leurs noms, `getSceneElements()` en
 * rendait trois juste après. **Deux secondes plus tard, la scène en portait zéro.**
 *
 * **LA CAUSE ÉCRITE ICI ÉTAIT FAUSSE, ET LA SOURCE DU PAQUET LE DÉMENT.** Elle accusait
 * l'effet de l'encre d'appeler `updateScene({ appState })` **sans `elements`**, « et le
 * moteur remplace alors la scène par du vide ». **Il ne la remplace pas** : lu dans
 * `dist/dev` en 0.18.1, le remplacement est gardé — `if (sceneData.elements)
 * this.scene.replaceAllElements(...)`. Un `updateScene` sans éléments ne touche donc pas
 * la scène.
 *
 * **Ce qui vidait réellement la scène était ailleurs** : les écritures partaient sur une
 * instance d'App **jetée** — le moteur en construit deux sous `StrictMode`, et le port
 * retenait la mauvaise. Voir la retenue différée en tête du composant.
 *
 * **C'est la forme la plus coûteuse de ce dépôt** : la mesure prise à l'instant du geste
 * disait vrai, et l'état d'après disait le contraire. La même leçon que « attends que ça
 * repose avant de mesurer un style », sur un autre objet.
 *
 * **Cette fonction n'est donc PAS la réparation qu'elle croyait être** — elle repassait
 * les éléments à chaque réglage pour se prémunir d'un effacement qui n'a jamais eu lieu.
 * Elle est conservée parce qu'elle est **inoffensive et centralisante** : tout réglage
 * d'état passe par un seul endroit. Ce qu'elle n'est plus, c'est **obligatoire**.
 */
function reglerLEtat(
  api: ExcalidrawImperativeAPI,
  etat: {
    readonly currentItemStrokeColor: string;
    readonly currentItemStrokeWidth: number;
    readonly currentItemOpacity: number;
  },
): void {
  // Le type du moteur exige un état COMPLET là où il en accepte un partiel à
  // l'exécution — même famille que la signature de l'export : l'affirmation est faite
  // ici, une fois, dans le port.
  api.updateScene({
    appState: etat as unknown as Parameters<
      ExcalidrawImperativeAPI['updateScene']
    >[0]['appState'],
  });
}

/**
 * **Ce que le masque de D-001 ferme, et ce qu'il laisse.**
 *
 * Fermé : le menu principal — c'est lui qui porte l'export natif et le dialogue Mermaid —,
 * l'îlot d'outils, et la boîte de propriétés d'un élément sélectionné, dont la palette
 * ferait une seconde source de couleurs à côté de `tokens.css`.
 *
 * Laissé : le contrôle de zoom, D-007.
 */
const MASQUE_DE_L_INTERFACE_NATIVE = `
  .surface-de-trace .excalidraw .App-menu__left,
  .surface-de-trace .excalidraw .layer-ui__wrapper__top-right,
  .surface-de-trace .excalidraw .App-toolbar,
  .surface-de-trace .excalidraw .App-toolbar__extra-tools-trigger,
  .surface-de-trace .excalidraw .dropdown-menu-button,
  .surface-de-trace .excalidraw .Island.App-menu__left,
  .surface-de-trace .excalidraw .mobile-misc-tools-container,
  .surface-de-trace .excalidraw .App-bottom-bar { display: none !important; }
`;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA SIGNATURE DE L'EXPORT EST DÉCLARÉE ICI, ET C'EST LE PORT QUI L'ASSUME.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `exportToSvg` est publié avec un type qui dépend de l'arbre de types internes du
 * paquet, **que notre configuration ne suit pas jusqu'au bout** : le compilateur passe,
 * et la règle `no-unsafe-call` refuse d'appeler une fonction dont le type ne se résout
 * pas. Elle a raison de la refuser — un appel non typé sur une frontière est exactement
 * ce qui a laissé passer le renommage de `auteurId` sur le canal.
 *
 * **Ce qui est fait à la place est de déclarer ce qu'on attend**, en un seul endroit :
 * `{ elements, appState, files, exportingFrame } → SVGSVGElement`, qui est le contrat
 * public documenté du paquet et celui sur lequel D-001 s'appuie. **Une seule ligne à
 * revoir le jour où le moteur change** — et c'est tout l'objet d'un port.
 */
const exporterUneCase = exportToSvg as unknown as (options: {
  readonly elements: readonly ExcalidrawElement[];
  readonly appState: {
    readonly exportBackground: boolean;
    readonly exportWithDarkMode: boolean;
  };
  readonly files: ReturnType<ExcalidrawImperativeAPI['getFiles']>;
  readonly exportingFrame: ExcalidrawElement;
}) => Promise<SVGSVGElement>;

/**
 * **Les pages non vides, en SVG, du haut vers le bas — F-25, Q341, Q371.**
 *
 * D-001 : « l'export PDF assemble les **exports SVG de chaque frame triée par position
 * Y** avec `pdf-lib` ». C'est la moitié de ce geste ; `pdf-lib` vit dans le document, pas
 * dans le moteur.
 *
 * **« Ce qui est tracé hors de la colonne ne part pas au document, et rien ne le
 * distingue à l'écran »** (Q345, Q346) : `exportingFrame` borne l'export à la case, donc
 * cette règle est tenue par le moteur et non par un filtre à écrire.
 */
async function exporterLesPages(
  api: ExcalidrawImperativeAPI,
): Promise<readonly PageExportee[]> {
  const scene = api.getSceneElements() as readonly ExcalidrawElement[];
  const cases = scene
    .filter(estUneCase)
    .slice()
    .sort((a, b) => a.y - b.y);
  const contenu = scene.filter((e) => !estUneCase(e));
  return pagesDesCases(cases, contenu, api.getFiles());
}

/** Le corps commun aux deux exports : la scène montée, et un tableau relu. */
async function pagesDesCases(
  cases: readonly ExcalidrawElement[],
  contenu: readonly ExcalidrawElement[],
  fichiers: ReturnType<ExcalidrawImperativeAPI['getFiles']> | undefined,
): Promise<readonly PageExportee[]> {
  const pages: PageExportee[] = [];

  for (const [index, page] of cases.entries()) {
    // **Une case vide est écartée** : « le document n'a jamais de page blanche »
    // (Q341). Le test est géométrique — un élément dont la boîte croise la case.
    const dedans = contenu.some(
      (e) =>
        e.x + (e.width ?? 0) > page.x &&
        e.x < page.x + (page.width ?? 0) &&
        e.y + (e.height ?? 0) > page.y &&
        e.y < page.y + (page.height ?? 0),
    );
    if (!dedans) continue;

    const svg = await exporterUneCase({
      // **LES CASES ELLES-MÊMES NE PARTENT PAS AU DOCUMENT, ET C'EST Q374.** « Le
      // document ne porte rien d'autre que les cases : aucun en-tête, aucun pied,
      // aucune page de garde. » La case est **le cadre** de la page, pas son contenu :
      // la laisser dans les éléments exportés imprimerait son trait gris clair sur
      // chaque page du document, c'est-à-dire un bord ajouté par le produit sur du
      // travail qui n'en porte pas.
      //
      // **Ce que ça ne change pas** : `exportingFrame` reçoit quand même la case, donc
      // c'est bien elle qui décide des dimensions de la page et de ce qui tombe dedans.
      elements: contenu,
      // La page reste blanche « y compris dans la structure sombre » : aucun fond
      // exporté, aucun thème. Le blanc est posé au tramage, par l'assembleur.
      appState: { exportBackground: false, exportWithDarkMode: false },
      // **Le dépôt de fichiers vient de la scène montée pour le tableau affiché, et
      // il est FABRIQUÉ pour les autres** — `exporterDesPages` charge les octets de
      // chaque image et les pose ici. C'est ce dépôt-là que l'export lit, donc une
      // image qui y figure part au document.
      files: fichiers ?? {},
      exportingFrame: page,
    });
    pages.push({
      rang: index + 1,
      svg: svg.outerHTML,
      largeur: PAGE_LARGEUR,
      hauteur: PAGE_HAUTEUR,
    });
  }
  return pages;
}

/**
 * **Poser une image sur la scène du moteur, à son identité de serveur.**
 *
 * Elle entre **en deux morceaux**, comme le moteur les attend : les octets dans son
 * dépôt de fichiers, et un élément qui les désigne. C'est ce dépôt-là que l'export lit
 * — `files: api.getFiles()` —, donc une image posée par ce chemin part au document.
 */
async function poserLImageSurLaScene(
  api: ExcalidrawImperativeAPI,
  image: ImageDeTableau,
): Promise<void> {
  if (image.adresse === null) return;
  const { source, largeur, hauteur } = await sourceDeLImage(image.adresse);

  const fichierId = `image-${image.id}`;
  api.addFiles([
    {
      id: fichierId,
      dataURL: source,
      mimeType: 'image/png',
      created: Date.now(),
    } as unknown as Parameters<ExcalidrawImperativeAPI['addFiles']>[0][number],
  ]);

  const place = image.positionX !== null && image.positionY !== null;
  const cadre = place
    ? {
        x: image.positionX ?? 0,
        y: image.positionY ?? 0,
        width: image.largeur ?? largeur,
        height: image.hauteur ?? hauteur,
      }
    : { ...placementDansLaVue(api, largeur, hauteur) };

  const fabriques = convertToExcalidrawElements([
    {
      id: image.id,
      type: 'image',
      fileId: fichierId,
      x: 'x' in cadre ? cadre.x : cadre.positionX,
      y: 'y' in cadre ? cadre.y : cadre.positionY,
      width: 'width' in cadre ? cadre.width : cadre.largeur,
      height: 'height' in cadre ? cadre.height : cadre.hauteur,
    } as unknown as Parameters<typeof convertToExcalidrawElements>[0] extends
      | readonly (infer S)[]
      | null
      ? S
      : never,
  ]);
  api.updateScene({ elements: [...api.getSceneElements(), ...fabriques] });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES PAGES D'UN TABLEAU QUE PERSONNE N'AFFICHE — Q372, LE DOCUMENT LES
 * PORTE TOUS.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-25 : « quand un cours a plusieurs tableaux, **le document les porte tous, dans
 * l'ordre où ils ont été ouverts** » (Q372). L'écran n'en affiche qu'un ; les autres
 * arrivent en éléments bruts dans la réponse de fabrication, et il faut les rendre en
 * pages **sans les monter**.
 *
 * **C'est possible parce que l'export du moteur est une fonction pure** : il prend des
 * éléments, une case et des fichiers, et rend un SVG. Aucune instance n'est nécessaire.
 *
 * **La grille est RECALCULÉE ici, pas reçue** — « l'origine de la grille est la même
 * pour tous les tableaux : c'est une constante du produit, et **elle ne se stocke
 * pas** » (Q342, Q344). Deux tableaux d'un même cours ont donc exactement la même
 * grille, et un tableau relu se pagine comme il se paginait.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **ELLE NE PORTAIT QUE LES TRACÉS JUSQU'AU 20260829, ET LES DEUX MOTIFS QUI
 * L'ÉCRIVAIENT ÉTAIENT PÉRIMÉS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Il était écrit qu'elle ne pouvait porter « ni images ni pense-bêtes, faute d'un dépôt
 * de fichiers et d'une scène montée ». **Aucune des deux absences n'était réelle** :
 * l'export du moteur reçoit son dépôt de fichiers **en argument**, `files`, et rien
 * n'oblige à ce qu'il vienne d'une instance ; et un pense-bête est un rectangle à label,
 * que `convertToExcalidrawElements` fabrique sans scène — c'est ce que fait déjà
 * `poserUnPenseBete`.
 *
 * **Ce qui manquait vraiment était au CONTRAT** : les pense-bêtes n'avaient au modèle ni
 * couleur ni position (D-123, le matin même), et la réponse de fabrication ne portait
 * pas d'adresse d'image (D-122). Les deux sont servis, donc les deux entrent ici.
 *
 * **LES OCTETS D'UNE IMAGE PASSENT PAR UNE BALISE ET UN CANEVAS, JAMAIS PAR UN APPEL
 * RÉSEAU.** `sourceDeLImage` charge l'adresse dans un `<img crossOrigin="anonymous">` et
 * lit ses pixels : c'est le mécanisme du tableau affiché, à l'identique, **et son repli
 * est le même** — sans `Access-Control-Allow-Origin` sur le contenant, le canevas se
 * salit, la lecture rend l'adresse brute, et **l'image manque au document sans que rien
 * d'autre échoue**. Le prérequis d'infrastructure est celui de D-122 ; il ne s'en ajoute
 * aucun.
 *
 * **Une image qui ne se charge pas n'emporte pas la page avec elle** : elle est écartée,
 * et le reste du tableau part.
 */
export async function exporterDesPages(
  elements: readonly ElementDeTableau[],
  images: readonly ImageDeTableau[] = [],
  penseBetes: readonly PenseBeteDeTableau[] = [],
): Promise<readonly PageExportee[]> {
  const traces = elements as unknown as readonly ExcalidrawElement[];

  const fichiers: Record<string, unknown> = {};
  const depuisLesImages: ExcalidrawElement[] = [];
  for (const image of images) {
    if (image.adresse === null) continue;
    let charge;
    try {
      charge = await sourceDeLImage(image.adresse);
    } catch {
      continue;
    }
    const fichierId = `image-${image.id}`;
    fichiers[fichierId] = {
      id: fichierId,
      dataURL: charge.source,
      mimeType: 'image/png',
      created: Date.now(),
    };
    depuisLesImages.push(
      ...(convertToExcalidrawElements([
        {
          id: image.id,
          type: 'image',
          fileId: fichierId,
          // **Une image sans placement se pose à l'origine de la grille**, et non au
          // centre d'une vue : il n'y a pas de vue ici. C'est le défaut borné aux
          // lignes écrites avant le 20260829, celles que D-123 corrige pour la suite.
          x: image.positionX ?? 0,
          y: image.positionY ?? 0,
          width: image.largeur ?? charge.largeur,
          height: image.hauteur ?? charge.hauteur,
        } as unknown as Parameters<typeof convertToExcalidrawElements>[0] extends
          | readonly (infer S)[]
          | null
          ? S
          : never,
      ]) as unknown as readonly ExcalidrawElement[]),
    );
  }

  const depuisLesPenseBetes = penseBetes.flatMap(
    (note) =>
      convertToExcalidrawElements([
        {
          id: note.id,
          type: 'rectangle',
          x: note.positionX ?? 0,
          y: note.positionY ?? 0,
          width: 220,
          height: 160,
          backgroundColor: note.couleur ?? 'transparent',
          fillStyle: 'solid',
          label: { text: note.texte, fontSize: 16 },
        } as unknown as Parameters<typeof convertToExcalidrawElements>[0] extends
          | readonly (infer S)[]
          | null
          ? S
          : never,
      ]) as unknown as readonly ExcalidrawElement[],
  );

  const contenu = [...traces, ...depuisLesImages, ...depuisLesPenseBetes];
  if (contenu.length === 0) return [];

  // **Le bas du contenu compte désormais les trois familles** : un tableau dont seule
  // une image descend bas aurait sinon perdu ses pages du bas, la grille s'arrêtant au
  // dernier tracé.
  const basDuContenu = contenu.reduce(
    (bas, e) => Math.max(bas, e.y + (e.height ?? 0)),
    0,
  );
  const cases = grille(
    Math.max(CASES_AU_DEPART, rangDeLOrdonnee(basDuContenu) + CASES_DE_MARGE),
  );
  return pagesDesCases(
    cases,
    contenu,
    fichiers as ReturnType<ExcalidrawImperativeAPI['getFiles']>,
  );
}

/**
 * **Annuler et refaire passent par le clavier du moteur, et c'est une limite de son API.**
 *
 * `ExcalidrawImperativeAPI` n'expose que `history.clear` : **il n'y a pas de `undo()`
 * public**. L'historique existe et il est **local à chaque poste**, ce qui est exactement
 * ce que le corpus demande — « l'annulation ne porte que sur ses propres gestes » (Q349),
 * et « si un élève efface tout, **lui seul peut le défaire** ». Le raccourci est donc la
 * seule voie, et les boutons de la barre d'outils le déclenchent.
 *
 * **Ce que ça coûte, écrit plutôt que caché** : si le moteur change de raccourci, ces deux
 * boutons cessent d'agir **sans que rien ne rougisse**. C'est le prix de l'absence d'API,
 * et il est nommé pour qu'on sache où regarder.
 */
function clavier(
  api: ExcalidrawImperativeAPI,
  touche: string,
  mods: { ctrl?: boolean; maj?: boolean },
): void {
  void api;
  document.dispatchEvent(
    new KeyboardEvent('keydown', {
      key: touche,
      code: `Key${touche.toUpperCase()}`,
      ctrlKey: mods.ctrl ?? false,
      metaKey: mods.ctrl ?? false,
      shiftKey: mods.maj ?? false,
      bubbles: true,
    }),
  );
}
